import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pause, Volume2 } from "lucide-react";

export const useExerciseManifest = () =>
  useQuery({ queryKey: ["exercise-manifest"], queryFn: () => fetch("/illustrations/manifest.json").then((r) => r.json()), staleTime: Infinity });

export const findExercise = (m, id) => {
  for (const family of m?.families || [])
    for (const pathology of family.pathologies)
      for (const exercise of pathology.exercises) if (exercise.id === id) return { family, pathology, exercise };
  return null;
};

const MODE_KEY = "bc.exerciseMode";
export const getExerciseMode = () => localStorage.getItem(MODE_KEY) || "patient";
export const setExerciseMode = (m) => localStorage.setItem(MODE_KEY, m);

export const useNetwork = () => {
  const read = () => {
    const c = navigator.connection || {};
    const online = navigator.onLine;
    const slow = c.saveData || ["cellular"].includes(c.type) || ["slow-2g", "2g", "3g"].includes(c.effectiveType);
    return { online, wifiOk: online && !slow };
  };
  const [net, setNet] = useState(read);
  useEffect(() => {
    const up = () => setNet(read());
    window.addEventListener("online", up); window.addEventListener("offline", up);
    navigator.connection?.addEventListener?.("change", up);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", up); navigator.connection?.removeEventListener?.("change", up); };
  }, []);
  return net;
};

/** Plays the step audio file when provided; falls back to on-device French speech synthesis (works offline). */
export const StepAudio = ({ src, text, stepKey }) => {
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  const stop = () => { audio.current?.pause(); window.speechSynthesis?.cancel(); setPlaying(false); };
  useEffect(() => stop, [stepKey]);
  const tts = () => {
    const s = window.speechSynthesis;
    if (!s) return setPlaying(false);
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "fr-FR"; u.rate = 0.9;
    const v = s.getVoices().find((x) => x.lang?.startsWith("fr"));
    if (v) u.voice = v;
    u.onend = u.onerror = () => setPlaying(false);
    s.speak(u);
  };
  const play = () => {
    if (playing) return stop();
    setPlaying(true);
    if (!src) return tts();
    audio.current = new Audio(src);
    audio.current.onended = () => setPlaying(false);
    audio.current.play().catch(tts);
  };
  return (
    <button data-testid="step-listen-button" onClick={play} aria-pressed={playing} aria-label={playing ? "Arrêter l'écoute" : "Écouter la consigne"}
      className="flex h-14 items-center gap-2 rounded-full bg-[#E0B33A] px-6 text-base font-extrabold text-[#0A2540] shadow-lg shadow-[#E0B33A]/25 transition-transform active:scale-95">
      {playing ? <Pause className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}{playing ? "Stop" : "Écouter"}
    </button>
  );
};
